import React, { useRef, useState } from 'react'

// An empty label prints the field's own label.
export interface ChosenField {
	key: string
	label: string
}

export interface PickerField {
	key: string
	label: string
}

export interface PickerList {
	id: string
	title: string
	items: ChosenField[]
	// A required list keeps at least one field.
	required?: boolean
}

interface Props {
	lists: PickerList[]
	available: PickerField[]
	onChange: (listId: string, items: ChosenField[]) => void
	// Called instead of onChange when one move changes two lists.
	onMove: (changes: Record<string, ChosenField[]>) => void
}

const AVAILABLE = '__available__'

/**
 * Pick, order and rename print fields. Drag a field between "Available fields" and a list
 * (or within a list to reorder), or click: a chip to add it, × to remove it.
 */
const PrintFieldPicker: React.FC<Props> = ({ lists, available, onChange, onMove }) => {
	const dragged = useRef<{ key: string; from: string } | null>(null)
	const [dropTarget, setDropTarget] = useState<{ listId: string; index: number } | null>(null)

	const labelOf = (key: string) => available.find((field) => field.key === key)?.label || key
	const unused = available.filter((field) => !lists.some((list) => list.items.some((item) => item.key === field.key)))
	const canRemove = (list: PickerList) => !(list.required && list.items.length === 1)

	const startDrag = (e: React.DragEvent, key: string, from: string) => {
		dragged.current = { key, from }
		e.dataTransfer.effectAllowed = 'move'
		e.dataTransfer.setData('text/plain', key)
	}

	const endDrag = () => {
		dragged.current = null
		setDropTarget(null)
	}

	const allowDrop = (e: React.DragEvent, listId: string, index: number) => {
		if (!dragged.current) {
			return
		}
		e.preventDefault()
		e.stopPropagation()
		if (dropTarget?.listId !== listId || dropTarget.index !== index) {
			setDropTarget({ listId, index })
		}
	}

	const dropOn = (e: React.DragEvent, listId: string, index: number) => {
		e.preventDefault()
		e.stopPropagation()
		const drag = dragged.current
		endDrag()
		if (!drag) {
			return
		}

		const source = lists.find((list) => list.id === drag.from)

		if (listId === AVAILABLE) {
			if (source && canRemove(source)) {
				onChange(source.id, source.items.filter((item) => item.key !== drag.key))
			}
			return
		}

		const target = lists.find((list) => list.id === listId)
		if (!target) {
			return
		}

		const moved = source?.items.find((item) => item.key === drag.key) || { key: drag.key, label: '' }

		if (source && source.id === target.id) {
			const from = target.items.findIndex((item) => item.key === drag.key)
			const items = target.items.filter((item) => item.key !== drag.key)
			items.splice(from < index ? index - 1 : index, 0, moved)
			onChange(target.id, items)
			return
		}

		if (source && !canRemove(source)) {
			return
		}

		const items = [...target.items]
		items.splice(index, 0, moved)
		if (source) {
			onMove({ [source.id]: source.items.filter((item) => item.key !== drag.key), [target.id]: items })
		} else {
			onChange(target.id, items)
		}
	}

	const step = (list: PickerList, index: number, by: number) => {
		const items = [...list.items]
		const [moved] = items.splice(index, 1)
		items.splice(index + by, 0, moved)
		onChange(list.id, items)
	}

	const dropLine = <div style={{ height: 2, background: '#ec008c', margin: '2px 0' }} />

	return (
		<div className='mb-3'>
			<div className='row g-3'>
				{lists.map((list) => (
					<div className={lists.length > 1 ? 'col-md-6' : 'col-12'} key={list.id}>
						<div
							className='border rounded p-2 h-100'
							style={{ minHeight: 70, background: dropTarget?.listId === list.id ? '#fff5fb' : undefined }}
							onDragOver={(e) => allowDrop(e, list.id, list.items.length)}
							onDrop={(e) => dropOn(e, list.id, list.items.length)}
						>
							<div className='fw-bolder fs-8 text-muted text-uppercase mb-2'>{list.title}</div>
							{list.items.map((item, index) => (
								<div
									key={item.key}
									onDragOver={(e) => allowDrop(e, list.id, index)}
									onDrop={(e) => dropOn(e, list.id, index)}
								>
									{dropTarget?.listId === list.id && dropTarget.index === index && dropLine}
									<div className='d-flex align-items-center gap-1 bg-light rounded px-2 py-1 mb-1'>
										<div
											className='d-flex align-items-center flex-shrink-0 fs-7'
											style={{ cursor: 'grab', width: lists.length > 1 ? '38%' : '30%', minWidth: 0 }}
											draggable
											onDragStart={(e) => startDrag(e, item.key, list.id)}
											onDragEnd={endDrag}
											title='Drag to move'
										>
											<span className='text-muted me-2' aria-hidden='true'>
												⠿
											</span>
											<span className='text-truncate'>{labelOf(item.key)}</span>
										</div>
										<input
											type='text'
											className='form-control form-control-sm py-1'
											maxLength={60}
											placeholder='Heading on the print'
											value={item.label}
											aria-label={`Heading for ${labelOf(item.key)}`}
											onChange={(e) =>
												onChange(
													list.id,
													list.items.map((other, i) => (i === index ? { ...other, label: e.target.value } : other))
												)
											}
										/>
										<button
											type='button'
											className='btn btn-sm btn-icon btn-light w-20px h-20px flex-shrink-0'
											disabled={index === 0}
											aria-label={`Move ${labelOf(item.key)} up`}
											onClick={() => step(list, index, -1)}
										>
											↑
										</button>
										<button
											type='button'
											className='btn btn-sm btn-icon btn-light w-20px h-20px flex-shrink-0'
											disabled={index === list.items.length - 1}
											aria-label={`Move ${labelOf(item.key)} down`}
											onClick={() => step(list, index, 1)}
										>
											↓
										</button>
										<button
											type='button'
											className='btn btn-sm btn-icon btn-light-danger w-20px h-20px flex-shrink-0'
											disabled={!canRemove(list)}
											aria-label={`Remove ${labelOf(item.key)}`}
											onClick={() => onChange(list.id, list.items.filter((other, i) => i !== index))}
										>
											×
										</button>
									</div>
								</div>
							))}
							{dropTarget?.listId === list.id && dropTarget.index === list.items.length && dropLine}
							{list.items.length === 0 && <div className='text-muted fs-8 py-2'>Drag a field here.</div>}
						</div>
					</div>
				))}
			</div>

			<div
				className='border border-dashed rounded p-2 mt-3'
				style={{ background: dropTarget?.listId === AVAILABLE ? '#fff5fb' : undefined }}
				onDragOver={(e) => allowDrop(e, AVAILABLE, 0)}
				onDrop={(e) => dropOn(e, AVAILABLE, 0)}
			>
				<div className='fw-bolder fs-8 text-muted text-uppercase mb-2'>
					Available fields{' '}
					<span className='fw-normal text-none'>
						— click or drag to add{lists.length > 1 ? ` (a click adds to ${lists[0].title.toLowerCase()})` : ''}, drop here to remove
					</span>
				</div>
				<div className='d-flex flex-wrap gap-2'>
					{unused.map((field) => (
						<button
							type='button'
							key={field.key}
							className='btn btn-sm btn-light-primary py-1 px-3'
							style={{ cursor: 'grab' }}
							draggable
							onDragStart={(e) => startDrag(e, field.key, AVAILABLE)}
							onDragEnd={endDrag}
							onClick={() => onChange(lists[0].id, [...lists[0].items, { key: field.key, label: '' }])}
						>
							+ {field.label}
						</button>
					))}
					{unused.length === 0 && <span className='text-muted fs-8'>Every field is on the print.</span>}
				</div>
			</div>
		</div>
	)
}

export default PrintFieldPicker
